export function round(num, decimalPlaces = 0) {
    if (num < 0) return -round(-num, decimalPlaces);
    var p = Math.pow(10, decimalPlaces);
    var n = num * p;
    var f = n - Math.floor(n);
    var e = Number.EPSILON * n;

    // Determine whether this fraction is a midpoint value.
    return f >= 0.5 - e ? Math.ceil(n) / p : Math.floor(n) / p;
}

export function firstLetterCapital(text) {
    if (!text) {
        return "";
    }
    return (
        text.slice(0, 1).toUpperCase() +
        text.slice(1, text.length).toLowerCase()
    );
}

export function singular(text) {
    // Permet de récupérer le singulier d'une chaine formatée "singulier / pluriel"
    // Ex. :
    // Test[s] => Test
    // {singulier}[pluriel] => singulier
    let regex = /(\[.*?\])|{|}/gi;
    return text.replace(regex, "");
}

export function plural(text) {
    // Permet de récupérer le pluriel d'une chaine formatée "singulier / pluriel"
    // Ex. :
    // Test[s] => Tests
    // {singulier}[pluriel] => pluriel
    let regex = /({.*?})|\[|\]/gi;
    return text.replace(regex, "");
}

export function intToDay(e) {
    // Entier vers libellé du jour
    const weekday = [
        "dimanche",
        "lundi",
        "mardi",
        "mercredi",
        "jeudi",
        "vendredi",
        "samedi",
    ];
    return weekday[e % 7];
}

export function intToShortDay(e) {
    // Entier vers libellé du jour (abrégé)
    const weekday = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
    return weekday[e % 7];
}

export function intToMonth(e) {
    // Entier vers libellé du mois
    const months = [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
    ];
    return months[(e - 1) % 12];
}

export function dateUTCToLocal(val) {
    // Converti une date UTC en date locale
    const aDate = new Date(val);
    return new Date(
        Date.UTC(
            aDate.getFullYear(),
            aDate.getMonth(),
            aDate.getDate(),
            aDate.getHours(),
            aDate.getMinutes(),
            aDate.getSeconds()
        )
    );
}

export function dateWithoutHour(val) {
    // Met une date à 00:00:00
    let aDate = val ? new Date(val) : new Date();
    aDate.setMilliseconds(0);
    aDate.setSeconds(0);
    aDate.setMinutes(0);
    aDate.setHours(0);
    return aDate;
}

export function firstDayOfWeek(val) {
    // Renvoi le lundi de la semaine de la date passée en paramètre.
    // Calcul en jours (via setDate) pour éviter les décalages dus aux changements d'heure (DST).
    const d = dateWithoutHour(val);
    const day = d.getDay(); // 0 (dim) -> 6 (sam)
    const offsetToMonday = (day + 6) % 7; // dim -> 6, lun -> 0, etc.
    d.setDate(d.getDate() - offsetToMonday);
    return d;
}

export function lastDayOfWeek(val) {
    // Renvoi le dimanche (23:59:59.999) de la semaine de la date passée en paramètre.
    // Calcul en jours (via setDate) pour éviter les décalages dus aux changements d'heure (DST).
    const d = firstDayOfWeek(val);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
}

export function dateToAAAAMMJJ(val, separator) {
    // Renvoi une date au format AAAAMMJJ en insérant "separator" (par ex. 2024-11-26)
    const aDate = val ? new Date(val) : new Date();
    return (
        String(aDate.getFullYear()) +
        (separator ?? "") +
        String(aDate.getMonth() + 1).padStart(2, "0") +
        (separator ?? "") +
        String(aDate.getDate()).padStart(2, "0")
    );
}

export function tomorrowWithoutHour(val) {
    // Renvoi le lendemain de la date passé en paramètre, à 00:00:00
    let aDate = dateWithoutHour(val);
    aDate.setTime(aDate.getTime() + 24 * 60 * 60 * 1000);
    return aDate;
}

export function isWeekend(val) {
    // Renvoi vrai si la date est un samedi ou dimanche, faux sinon.
    let aDate = dateWithoutHour(val);
    const aDay = aDate.getDay();
    return (aDay === 0) | (aDay === 6);
}

export function colorIsDark(hexColor) {
    // Renvoi vrai si la couleur est "sombre", faux sinon (permet d'adapter la couleur du texte en fonction du fond)
    const normalized =
        typeof hexColor === "string" && hexColor.startsWith("#")
            ? hexColor
            : `#${hexColor ?? ""}`;

    if (!/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
        return false;
    }

    const r = "0x" + normalized[1] + normalized[2];
    const g = "0x" + normalized[3] + normalized[4];
    const b = "0x" + normalized[5] + normalized[6];
    return (parseInt(r) + parseInt(g) + parseInt(b)) / 127 < 3;
}

export function getWeekNumber(val) {
    // Renvoi le numéro de semaine
    const dateUTC = new Date(
        Date.UTC(val.getFullYear(), val.getMonth(), val.getDate())
    );
    const sunday = new Date(dateUTC);
    if (sunday.getDay() != 0) {
        sunday.setDate(sunday.getDate() + 7 - sunday.getDay());
    }
    const mondayWeekOne = new Date(sunday.getFullYear(), 0, 4);
    mondayWeekOne.setDate(
        mondayWeekOne.getDate() -
        (mondayWeekOne.getDay() === 0 ? 7 : mondayWeekOne.getDay()) +
        1
    );
    if (dateUTC < mondayWeekOne) return 53;
    const timeBetween = dateUTC.getTime() - mondayWeekOne.getTime();
    const dayNumberInYear = timeBetween / (24 * 3600000); // 0~364/365
    const weekNumber = 1 + Math.floor(dayNumberInYear / 7);
    return weekNumber;
}

export function getDatesOfWeek(val) {
    // Renvoi les jours suivants de la semaine correspondants à la date passée en paramètre (la date passée sera incluse, mais les jours précédents éventuels non si la date > lundi)
    const dateUTC = new Date(
        Date.UTC(val.getFullYear(), val.getMonth(), val.getDate())
    );
    const listDates = [
        new Date(
            dateUTC.getFullYear(),
            dateUTC.getMonth(),
            dateUTC.getDate(),
            0,
            0,
            0,
            0
        ),
    ];
    let numDay = 1;
    while (
        (numDay < 7) &
        (new Date(
            dateUTC.getFullYear(),
            dateUTC.getMonth(),
            dateUTC.getDate() + numDay,
            0,
            0,
            0,
            0
        ).getDay() !=
            1)
    ) {
        listDates.push(
            new Date(
                dateUTC.getFullYear(),
                dateUTC.getMonth(),
                dateUTC.getDate() + numDay,
                0,
                0,
                0,
                0
            )
        );
        numDay++;
    }
    return listDates;
}

export const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}h${minutes}`;
};

export const formatDate = (date) => {
    const joursSemaine = [
        "dimanche",
        "lundi",
        "mardi",
        "mercredi",
        "jeudi",
        "vendredi",
        "samedi",
    ];
    const moisAnnee = [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
    ];

    return `${date.getDate()} ${moisAnnee[date.getMonth()]
        } ${date.getFullYear()}`;
};

export const getEventStatus = (event) => {
    const statuses = event ? event.shifts.map((shift) => shift.status) : [];
    let status = { PENDING: false, CLOSED: false, CANCELLED: false };
    if (statuses.includes("PENDING")) {
        status["PENDING"] = true;
    }
    if (statuses.includes("CLOSED")) {
        status["CLOSED"] = true;
    }
    if (statuses.includes("CANCELLED")) {
        status["CANCELLED"] = true;
    }
    return status;
};

export function addDuration(date, duration) {
    // la durée "duration" doit être au format HH:MM, cette durée sera ajoutée à "date"
    const newDate = new Date(date.toUTCString());
    const aHour = duration.slice(0, 2);
    const aMin = duration.slice(3, 5);
    newDate.setHours(newDate.getHours() + Number(aHour));
    newDate.setMinutes(newDate.getMinutes() + Number(aMin));
    return newDate;
}

export function getDuration(start, end, format = "HH:MM") {
    // Ecart entre start et end 

    // Retour en heures/minutes
    if (format === "HH:MM") {
        const aDuree = new Date(end) - new Date(start);
        const aHour = Math.floor(aDuree / 3600000);
        const aMin = Math.round((aDuree % 3600000) / 60000);
        return ("00" + aHour).slice(-2) + ":" + ("00" + aMin).slice(-2);
    } else {
        // Retour en heures/centième
        return ((new Date(end) - new Date(start)) / 3600000);
    }
}

export function floatRoundToString (val, decimal = 2) {
    const rounded = Math.round(val * (10 ** decimal)) / (10 ** decimal);
    return `${rounded}`.replace(".", ",");
};

// --- REGEX de vérification ---
export function validatePhone(val) {
    const regex = /^(?:\+33|0)[1-9](?:\d{2}){4}$/;
    return regex.test(val.replace(/\s+/g, ""))
}

export function validateEmail(val) {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(val)
}